import React, {useContext, useEffect, useMemo, useState} from 'react';
import {PAContext} from "../../../Services/PAContext";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import duration from 'dayjs/plugin/duration';

import {
    Avatar, AvatarGroup,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CardMedia,
    Chip, Divider,
    Menu,
    MenuItem,
    Tooltip
} from "@mui/material";

import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import './activities.css';
import ListItemIcon from "@mui/material/ListItemIcon";
import {
    Timeline,
    TimelineContent,
    TimelineItem,
    timelineItemClasses, TimelineOppositeContent, timelineOppositeContentClasses
} from "@mui/lab";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import {capitalizeFirstLetter, formatName, formatTime, stringAvatar, stringToColor} from "../../Helpers/TextStyling";

import Rating from "@mui/material/Rating";

import ActivityDialogEditAttendees from "./ActivityDialogEditAttendees";
import ActivityDialogEditCosts from "./ActivityDialogEditCosts";
import ActivityDialogEditDates from "./ActivityDialogEditDates";
import ActivityDialogEditPhotos from "./ActivityDialogEditPhotos";
import ActivityDialogEditLocation from "./ActivityDialogEditLocation";
import ActivityDialogEditDetails from "./ActivityDialogEditDetails";
import {getAllContactsFromDatabase} from "../../Restricted/Contacts/BrowserDatabaseFunctions";

dayjs.extend(duration);


function ActivitiesCard(props){


    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();

    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account, SampleAttendees, authenticated } = dataArray;

    const [dbContacts, setDbContacts] = useState([]);
    useEffect(() => {
        if (!authenticated) {
            setDbContacts([]);
            return;
        }

        let cancelled = false;

        getAllContactsFromDatabase()
            .then(contacts => {
                if (cancelled) return;
                setDbContacts(Array.isArray(contacts) ? contacts : []);
            })
            .catch(err => {
                console.error('Failed to load Contacts from browser DB:', err);
                if (!cancelled) {
                    setDbContacts([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [authenticated]);

    const [ attendeesReferenceArray, setAttendeesReferenceArray ] = useState( authenticated
        ? dbContacts
        : SampleAttendees);

    useEffect(() => {

        console.log("authenticated, dbContacts, SampleAttendees triggered ...");

        setAttendeesReferenceArray(
            authenticated
                ? dbContacts
                : SampleAttendees
        );
    }, [authenticated, dbContacts, SampleAttendees]);

    const [eventActivity, setEventActivity] = useState(props.activity);

    function deepEqual(a, b) {
        if (a === b) return true;
        if (Number.isNaN(a) && Number.isNaN(b)) return true;
        if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
        if (!a || !b || typeof a !== "object" || typeof b !== "object") return false;

        const aIsArr = Array.isArray(a), bIsArr = Array.isArray(b);
        if (aIsArr !== bIsArr) return false;

        if (aIsArr) {
            if (a.length !== b.length) return false;
            for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false;
            return true;
        }

        const aKeys = Object.keys(a), bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) return false;
        for (const k of aKeys) if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
        for (const k of aKeys) if (!deepEqual(a[k], b[k])) return false;
        return true;
    }

    function deepMergePreserveExtras(base, patch) {
        if (patch === null) return null;
        const baseIsObj = base && typeof base === "object";
        const patchIsObj = patch && typeof patch === "object";
        if (!baseIsObj || !patchIsObj) return patch;

        if (Array.isArray(base) || Array.isArray(patch)) {
            return Array.isArray(patch) ? patch.slice() : patch;
        }

        const out = { ...base };
        for (const key of Object.keys(patch)) {
            const merged = deepMergePreserveExtras(base[key], patch[key]);
            if (merged === null) {
                if (key in out) delete out[key];
            } else {
                out[key] = merged;
            }
        }
        return out;
    }

    useEffect(() => {
        const merged = deepMergePreserveExtras(eventActivity, props.activity);
        if (!deepEqual(merged, eventActivity)) {
            setEventActivity(merged);
        }
    }, [props.activity]);

    const [eventActivityPhotos, setEventActivityPhotos] = useState([]);


    const photos = eventActivity.attendee_photos?.length > 0
        ? eventActivity.attendee_photos
        : eventActivity.photos || [];

    const [visiblePhotos, setVisiblePhotos] = useState(() => photos.map(() => true));

    // Reset visibility if photo list changes
    useEffect(() => {
        setVisiblePhotos(photos.map(() => true));
    }, [photos]);

    const calculateElapsedTime = (beginDate, endDate, beginTime, endTime, daysCount = null) => {
        console.log("calculateElapsedTime");

        // Parse begin and end dates
        beginDate = beginDate ? dayjs(beginDate) : null;
        endDate = endDate ? dayjs(endDate) : beginDate;

        // Parse times
        const parsedBeginTime = beginTime ? dayjs(beginTime, "h:mm A") : null;
        const parsedEndTime = endTime ? dayjs(endTime, "h:mm A") : null;

        // Guard clause for missing values
        if (!beginDate || !parsedBeginTime || !parsedEndTime) {
            console.warn("Missing required date/time values.");
            return "0 m";
        }

        // Combine dates with times
        let beginDateTime = beginDate.hour(parsedBeginTime.hour()).minute(parsedBeginTime.minute());
        let endDateTime = endDate.hour(parsedEndTime.hour()).minute(parsedEndTime.minute());

        // If end time is 12:00 AM and not ahead of start, treat it as next day
        if (
            parsedEndTime.format("h:mm A") === "12:00 AM" &&
            endDateTime.isSameOrBefore(beginDateTime)
        ) {
            endDateTime = endDateTime.add(1, "day");
        }

        // Compute duration
        const elapsedMilliseconds = endDateTime.diff(beginDateTime);
        if (elapsedMilliseconds < 0) {
            console.warn("End time is before start time, returning '0 m'.");
            return "0 m";
        }

        const duration = dayjs.duration(elapsedMilliseconds);
        const days = Math.floor(duration.asDays());
        const hours = duration.hours();
        const minutes = duration.minutes();

        // Format result
        const parts = [];
        if (days > 0) parts.push(`${days} day${days !== 1 ? "s" : ""}`);
        if (hours > 0) parts.push(`${hours} hr${hours !== 1 ? "s" : ""}`);
        if (minutes > 0) parts.push(`${minutes} min${minutes !== 1 ? "s" : ""}`);

        return parts.join(" ") || "0 m";
    };

    // At the top of your component (or right before return if it uses eventActivity):
    const duration = useMemo(() => {
        const { time_table } = eventActivity;

        if (time_table?.dates?.length > 0) {
            const firstEntry = time_table.dates[0];
            const lastEntry = time_table.dates[time_table.dates.length - 1];

            if (firstEntry['all-day']) return 'All Day';

            const beginDate = dayjs(firstEntry.date, "YYYY-MM-DD");
            const endDate = dayjs(lastEntry.date, "YYYY-MM-DD");
            const beginTime = firstEntry['begin_time'] ? dayjs(firstEntry['begin_time'], "h:mm A") : null;
            const endTime = lastEntry['end_time'] ? dayjs(lastEntry['end_time'], "h:mm A") : null;

            if (!beginTime?.isValid() || !endTime?.isValid()) return '';

            return calculateElapsedTime(
                beginDate,
                endDate,
                beginTime.format('h:mm A'),
                endTime.format('h:mm A')
            );
        }

        if (time_table?.days?.length > 0) {
            const firstDayEntry = time_table.days[0];
            const lastDayEntry = time_table.days[time_table.days.length - 1];

            if (firstDayEntry['all-day']) {
                return time_table.days.length === 1 ? 'All Day' : `${time_table.days.length} Days`;
            }

            const beginTime = firstDayEntry['begin_time'] ? dayjs(firstDayEntry['begin_time'], "h:mm A") : null;
            const endTime = lastDayEntry['end_time'] ? dayjs(lastDayEntry['end_time'], "h:mm A") : null;

            if (!beginTime?.isValid() || !endTime?.isValid()) return '';

            const daysCount = time_table.days.length;

            return calculateElapsedTime(
                dayjs().startOf('day'),
                dayjs().startOf('day').add(daysCount - 1, 'day'),
                beginTime,
                endTime,
                daysCount
            );
        }

        return '';
    }, [eventActivity]);

    const [anchorActivityListItemMenu, setAnchorActivityListItemMenu] = useState(null);
    const handleActivityListItemMenuOpenMenu = (event) => {
        setAnchorActivityListItemMenu(event.currentTarget);
    };
    const handleActivityListItemMenuClose = () => {
        setAnchorActivityListItemMenu(null);
    };


    const [isActivitiesDialogEditAttendeesOpen, setActivitiesDialogEditAttendeesOpen] = useState(false);
    const [isActivitiesDialogEditCostsOpen, setActivitiesDialogEditCostsOpen] = useState(false);
    const [isActivitiesDialogEditPhotosOpen, setActivitiesDialogEditPhotosOpen] = useState(false);
    const [isActivitiesDialogEditDatesOpen, setActivitiesDialogEditDatesOpen] = useState(false);
    const [isActivitiesDialogEditLocationOpen, setActivitiesDialogEditLocationOpen] = useState(false);
    const [isActivitiesDialogEditDetailsOpen, setActivitiesDialogEditDetailsOpen] = useState(false);

    useEffect(() => {
        if (eventActivity['location'].google_place_id && eventActivityPhotos.length === 0) {
            fetchPlacePhotos(eventActivity['location'].google_place_id);
        }
    }, [eventActivity['location'].google_place_id]);

    const fetchPlacePhotos = async (placeId) => {
        // 1. Initialize the Place object
        const { Place } = await window.google.maps.importLibrary("places");
        const place = new Place({
            id: placeId,
        });

        // 2. Fetch only the 'photos' field
        // This is a Promise-based call, so we use 'await'
        try {
            await place.fetchFields({ fields: ['photos'] });

            if (place.photos && place.photos.length > 0) {
                const urls = place.photos.map(p => p.getURI({ maxWidth: 150, maxHeight: 150 }));
                console.log("urls : ", urls);
                setEventActivityPhotos(urls);
            }
        } catch (error) {
            console.error("Error fetching place photos:", error);
        }
    };


    const handleMenuItemClick = (_item_name) => {
        // 1. Close the 3-dot menu and set the visual action state
        setAnchorActivityListItemMenu(null);

        if (_item_name === 'Delete') {
            //prompt for confirmation
            props.onDeleteActivity();
        }

        // 3. Dispatch the correct Dialog opener
        switch (_item_name) {
            case "Attendees":
                setActivitiesDialogEditAttendeesOpen(!isActivitiesDialogEditAttendeesOpen);
                break;

            case "Costs":
                setActivitiesDialogEditCostsOpen(!isActivitiesDialogEditCostsOpen);
                break;

            case "Photos":
                setActivitiesDialogEditPhotosOpen(!isActivitiesDialogEditPhotosOpen);
                break;

            case "Dates":
                setActivitiesDialogEditDatesOpen(!isActivitiesDialogEditDatesOpen);
                break;

            case "Location":
                setActivitiesDialogEditLocationOpen(!isActivitiesDialogEditLocationOpen);
                break;

            case "Details":
                setActivitiesDialogEditDetailsOpen(!isActivitiesDialogEditDetailsOpen);
                break;
            default:

        }
    };

    const handleSaveActivityUpdate = (updatedActivity) => {
        // 1. Update local state
        setEventActivity(updatedActivity);

        // 🔄 Check if any attendees in the updated activity are not yet part of the global Planning.attendees array
        const globalAttendees = usePA_GlobalStore.getState().PA_GlobalObject.Planning.attendees;
        const newActivityAttendees = eventActivity.attendees || [];

        const attendeesToAdd = newActivityAttendees
            .filter(newAtt => !globalAttendees.some(existing => existing.id === newAtt.id))
            .map(newAtt => {
                // Pull from reference array to ensure full attendee details are used
                return attendeesReferenceArray.find(ref => ref.id === newAtt.id);
            })
            .filter(Boolean); // Remove nulls if any attendee isn't found in reference

        // 🧠 Add these new attendees to global Planning.attendees before updating the activity
        set_PA_GlobalObject((prev) => ({
            Planning: {
                ...prev.Planning,
                attendees: attendeesToAdd.length > 0
                    ? [...prev.Planning.attendees, ...attendeesToAdd]
                    : prev.Planning.attendees,
                activities: prev.Planning.activities.map((l) =>
                    l.id === updatedActivity.id ? { ...l, ...updatedActivity } : l
                ),
            },
        }));

        if (PA_GlobalObject.Planning.status !== 10){
            //send updated activity to server
            const _sendString = {};
            _sendString.action = "E_updateEventActivity_v2";
            _sendString.event_id = PA_GlobalObject.Planning.id;
            _sendString.activity = updatedActivity;

            sendSocketMessage(_sendString);

        }
    };


    return (


        <div>

            <Card
                sx={{
                    borderRadius: '16px',
                    // 🟢 PREMIUM GLASSMORPHISM: Frosted glass effect with sharp reflective borders
                    background: 'rgba(255, 255, 255, 0.4)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.08)',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        boxShadow: '0 8px 32px 0 rgba(240, 150, 55, 0.15)',
                        borderColor: 'rgba(240, 150, 55, 0.4)'
                    }
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                    <Box sx={{ flexGrow: 1, position: 'relative' }}>

                        {/* 📸 IMAGE AREA SECTION */}
                        {(
                            eventActivity['attendee_photos'] && eventActivity['attendee_photos'].length > 0 ? (
                                <Box sx={{
                                    p: '12px 12px 0 12px',
                                    '& .horizontal-scroll-container': {
                                        display: 'flex', gap: '8px', overflowX: 'auto', pb: '4px',
                                        '&::-webkit-scrollbar': { height: '4px' },
                                        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '4px' }
                                    }
                                }}>
                                    <div className="activities-suggestions-add-list-item-photos">
                                        <div className="horizontal-scroll-container">
                                            {
                                                (eventActivity['attendee_photos']).map((photo, index) => {
                                                    const imageSrc = PA_GlobalObject.Planning.photos.find(_p => _p.id === photo.photo_id)?.thumbnail_data || PA_GlobalObject.Planning.photos.find(_p => _p.id === photo.photo_id)?.full_data;

                                                    return visiblePhotos[index] ? (
                                                        <CardMedia
                                                            key={index}
                                                            component="img"
                                                            alt={`Photo ${index + 1}`}
                                                            image={imageSrc}
                                                            sx={{ width: 120, height: 120, borderRadius: '8px', objectFit: 'cover' }}
                                                            onError={() =>
                                                                setVisiblePhotos(prev => {
                                                                    const updated = [...prev];
                                                                    updated[index] = false;
                                                                    return updated;
                                                                })
                                                            }
                                                        />
                                                    ) : null;
                                                })
                                            }
                                        </div>
                                    </div>
                                </Box>
                            ) : eventActivityPhotos && eventActivityPhotos.length > 0 ? (
                                <Box sx={{
                                    p: '12px 12px 0 12px',
                                    '& .horizontal-scroll-container': {
                                        display: 'flex', gap: '8px', overflowX: 'auto', pb: '4px',
                                        '&::-webkit-scrollbar': { height: '4px' },
                                        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '4px' }
                                    }
                                }}>
                                    <div className="activities-suggestions-add-list-item-photos">
                                        <div className="horizontal-scroll-container">
                                            {eventActivityPhotos.map((url, index) => (
                                                <CardMedia
                                                    key={index}
                                                    component="img"
                                                    alt={`Photo ${index + 1}`}
                                                    image={url}
                                                    sx={{ width: 120, height: 120, borderRadius: '8px', objectFit: 'cover' }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </Box>
                            ) : null
                        )}

                        {/* 🏷️ HEADER CONTAINER */}
                        <CardHeader
                            title={(() => {
                                const { time_table } = eventActivity;
                                if (!time_table) return '';
                                if (!time_table?.dates?.length) {
                                    if (time_table.days && time_table.days.length > 0) {
                                        const _entry = time_table.days[0];
                                        const key = Object.keys(_entry).find(k => k.startsWith("day "));
                                        const dayName = _entry[key];
                                        if (key && dayName) {
                                            const shortDay = dayName.slice(0, 3);
                                            return `${shortDay}, ${capitalizeFirstLetter(key)}`;
                                        }
                                    }
                                    return '';
                                }
                                const firstDate = time_table.dates[0]?.date || null;
                                const lastDate = time_table.dates[time_table.dates.length - 1]?.date || null;
                                const startDateObj = firstDate ? dayjs(firstDate, "YYYY-MM-DD") : null;
                                const endDateObj = lastDate ? dayjs(lastDate, "YYYY-MM-DD") : null;

                                if (!startDateObj?.isValid() || !endDateObj?.isValid()) return '';

                                const formattedStartDate = startDateObj.format('ddd, MMM D');
                                const formattedEndDate = !startDateObj.isSame(endDateObj, 'day')
                                    ? ` - ${endDateObj.format('ddd, MMM D')}`
                                    : '';
                                return `${formattedStartDate}${formattedEndDate}`;
                            })()}
                            action={(() => {
                                const _att = attendeesReferenceArray.find(_att => _att.id === Account?.id);
                                const userAttendees = Array.isArray(eventActivity?.attendees) ? eventActivity.attendees : [];
                                const canShowMenu = _att?.role === "planner" || _att?.role === "master_admin" || _att?.role === "admin" || userAttendees.some(_a => _a.id === Account?.id);

                                if (!canShowMenu) return null;

                                return (
                                    <IconButton onClick={handleActivityListItemMenuOpenMenu} sx={{ color: 'text.secondary' }}>
                                        <i className="icon-menu2" style={{ fontSize: '16px' }} />
                                    </IconButton>
                                );
                            })()}
                            sx={{
                                p: '16px 16px 8px 16px',
                                "& .MuiCardHeader-title": {
                                    fontSize: "1.05rem",
                                    fontWeight: 700,
                                    color: '#2c3e50'
                                },
                            }}
                        />


                        {/* ⏱️ DURATION BLOCK */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: '16px', mb: 1 }}>
                            {duration && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'rgba(0,0,0,0.04)', px: 1, py: 0.25, borderRadius: '4px' }}>
                                    Duration: {duration}
                                </Typography>
                            )}
                        </Box>

                        {/* 🗺️ TIMELINE & CONTENT */}
                        <CardContent sx={{ pt: 0, pb: '60px !important' }}>
                            <Timeline
                                sx={{
                                    [`& .${timelineOppositeContentClasses.root}`]: { flex: 0.3, textAlign: 'left', px: 0 },
                                    [`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 },
                                    padding: 0, mb: 2
                                }}
                            >
                                <TimelineItem sx={{ minHeight: 0 }}>
                                    <TimelineOppositeContent>
                                        {eventActivity.time_table?.dates?.length > 0 ? (
                                            eventActivity.time_table.dates[0]['all-day'] ? (
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#f09637' }}>All Day</Typography>
                                            ) : (
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    {eventActivity.time_table.dates[0].begin_time && (
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>{formatTime(eventActivity.time_table.dates[0].begin_time)}</Typography>
                                                    )}
                                                    {eventActivity.time_table.dates[eventActivity.time_table.dates.length - 1].end_time && (
                                                        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>{formatTime(eventActivity.time_table.dates[eventActivity.time_table.dates.length - 1].end_time)}</Typography>
                                                    )}
                                                </Box>
                                            )
                                        ) : eventActivity.time_table?.days?.length > 0 ? (
                                            eventActivity.time_table.days[0]['all-day'] ? (
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#f09637' }}>All Day</Typography>
                                            ) : (
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    {eventActivity.time_table.days[0].begin_time && (
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>{formatTime(eventActivity.time_table.days[0].begin_time)}</Typography>
                                                    )}
                                                    {eventActivity.time_table.days[eventActivity.time_table.days.length - 1].end_time && (
                                                        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>{formatTime(eventActivity.time_table.days[eventActivity.time_table.days.length - 1].end_time)}</Typography>
                                                    )}
                                                </Box>
                                            )
                                        ) : null}
                                    </TimelineOppositeContent>

                                    <TimelineContent sx={{ borderLeft: '2px solid #f09637', pl: 2, pr: 0, py: 0, ml: 1.5 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#2c3e50', mb: 0.5 }} variant="body2">
                                            {eventActivity['title']}
                                        </Typography>

                                        {eventActivity['location'] && (
                                            <Box
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const loc = eventActivity['location'];
                                                    // Build search string using place name + full address details for best search results
                                                    const searchQuery = [
                                                        loc.name,
                                                        loc.address,
                                                        loc.city,
                                                        loc.state,
                                                        loc.zip
                                                    ].filter(Boolean).join(' ');

                                                    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`;
                                                    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
                                                }}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 0.25,
                                                    cursor: 'pointer',
                                                    width: 'fit-content',
                                                    '&:hover .map-link': {
                                                        textDecoration: 'underline',
                                                        color: '#1e293b'
                                                    }
                                                }}
                                            >
                                                {eventActivity['location'].name && (
                                                    <Typography className="map-link" sx={{ fontSize: '13px', fontWeight: 600 }} color="text.secondary">
                                                        {eventActivity['location'].name}
                                                    </Typography>
                                                )}

                                                {eventActivity['location'].address && (
                                                    <Typography className="map-link" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                                        {eventActivity['location'].address}
                                                    </Typography>
                                                )}

                                                {(eventActivity['location'].city || eventActivity['location'].state || eventActivity['location'].zip) && (
                                                    <Typography className="map-link" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                                        {[
                                                            eventActivity['location'].city,
                                                            eventActivity['location'].state,
                                                            eventActivity['location'].zip
                                                        ].filter(Boolean).join(' ')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </TimelineContent>
                                </TimelineItem>
                            </Timeline>

                            {/* 🌐 QUICK LINKS SECTION */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, mt: 1, pl: '4px' }}>
                                {eventActivity['location'].website && (
                                    <Box onClick={() => window.open(eventActivity['location'].website, '_blank')} sx={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: '#1976d2', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                                        <i className="icon-network" style={{ marginRight: '6px' }}/>
                                        <span style={{ fontWeight: 600 }}>Website</span>
                                    </Box>
                                )}

                                {Array.isArray(eventActivity['location'].hours) && eventActivity['location'].hours.length > 0 && (
                                    <Box onClick={() => alert(eventActivity['location'].hours.map((line) => line.replace(/12:00 AM/g, 'midnight').replace(/12:00 PM/g, 'noon')).join('\n'))} sx={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: '#1976d2', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                                        <i className="icon-clock" style={{ marginRight: '6px' }}/>
                                        <span style={{ fontWeight: 600 }}>View Hours</span>
                                    </Box>
                                )}
                            </Box>

                            {/* ⭐ RATINGS ROW */}
                            {eventActivity['location'].total_ratings > 0 && (
                                <Box sx={{ display: "flex", alignItems: "center", bgcolor: 'rgba(0,0,0,0.03)', p: '6px 12px', borderRadius: '8px', width: 'fit-content', mb: 2 }}>
                                    <Rating name="read-only" value={eventActivity['location'].rating} precision={0.1} readOnly size="small" />
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, ml: 1, color: 'text.secondary' }}>
                                        ({eventActivity['location'].total_ratings} review{eventActivity['location'].total_ratings === 1 ? '' : 's'})
                                    </Typography>
                                </Box>
                            )}

                            {/* 📝 CONDITIONAL NOTES CONTAINER */}
                            {(
                                eventActivity.notes ||
                                eventActivity.details ||
                                (eventActivity.bring && eventActivity.bring.length > 0) ||
                                eventActivity.booked_through ||
                                eventActivity.confirmation_number ||
                                eventActivity.reservation_name ||
                                eventActivity.reservation_number ||
                                eventActivity.website
                            ) && (
                                <Box
                                    sx={{
                                        // Glass Sub-panel styling
                                        backgroundColor: 'rgba(255, 255, 255, 0.45)',
                                        border: '1px solid rgba(255,255,255,0.5)',
                                        borderRadius: '12px',
                                        p: 1.5,
                                        mt: 1.5,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1
                                    }}
                                >
                                    {eventActivity.notes?.length > 0 && (
                                        <Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
                                            <strong style={{ color: '#2c3e50' }}>Notes:</strong> {eventActivity.notes}
                                        </Typography>
                                    )}
                                    {eventActivity.details?.length > 0 && (
                                        <Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
                                            <strong style={{ color: '#2c3e50' }}>Details:</strong> {eventActivity.details}
                                        </Typography>
                                    )}
                                    {eventActivity.booked_through?.length > 0 && (
                                        <Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
                                            <strong style={{ color: '#2c3e50' }}>Booked Through:</strong> {eventActivity.booked_through}
                                        </Typography>
                                    )}
                                    {eventActivity.confirmation_number?.length > 0 && (
                                        <Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
                                            <strong style={{ color: '#2c3e50' }}>Confirmation#:</strong> {eventActivity.confirmation_number}
                                        </Typography>
                                    )}
                                    {eventActivity.reservation_name?.length > 0 && (
                                        <Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
                                            <strong style={{ color: '#2c3e50' }}>Reservation Name:</strong> {eventActivity.reservation_name}
                                        </Typography>
                                    )}
                                    {eventActivity.reservation_number?.length > 0 && (
                                        <Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
                                            <strong style={{ color: '#2c3e50' }}>Reservation#:</strong> {eventActivity.reservation_number}
                                        </Typography>
                                    )}
                                    {eventActivity.website?.length > 0 && (
                                        <Typography variant="caption" sx={{ display: "block", width: 'fit-content', textDecoration: 'underline', fontWeight: 700 }} color="primary" component="a" href={eventActivity.website} target="_blank" rel="noopener noreferrer">
                                            Website Link
                                        </Typography>
                                    )}

                                    {/* Pack List Chips */}
                                    {eventActivity.bring?.length > 0 && (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: -0.25 }}>
                                                <strong style={{ color: '#2c3e50' }}>Bring:</strong>
                                            </Typography>
                                            {(typeof eventActivity.bring === 'string' ? eventActivity.bring.split(',') : eventActivity.bring)
                                                .map(item => item.trim())
                                                .filter(item => item.length > 0)
                                                .map((item, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={item}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: 'rgba(255,255,255,0.7)',
                                                            border: '1px solid rgba(0,0,0,0.06)',
                                                            borderRadius: '6px',
                                                            fontSize: '11px',
                                                            fontWeight: 600
                                                        }}
                                                    />
                                                ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </CardContent>

                        {/* 👥 AVATAR ATTENDEE FOOTER TRACKER */}
                        <CardActions sx={{ position: 'absolute', bottom: '8px', right: '12px', p: 0 }}>
                            <AvatarGroup
                                max={10}
                                sx={{
                                    '.MuiAvatar-root': {
                                        zIndex: (theme) => theme.zIndex.drawer + 1,
                                        marginRight: '-6px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        borderRadius: '50%',
                                        backgroundColor: '#fff',
                                        color: 'text.primary',
                                        fontWeight: '600',
                                        fontSize: '12px',
                                        width: '28px',
                                        height: '28px',
                                    }
                                }}
                            >
                                {eventActivity.attendees
                                    .sort((a, b) => {
                                        const a_attendee = PA_GlobalObject.Planning.attendees.find((_att) => _att.id === a.id);
                                        const b_attendee = PA_GlobalObject.Planning.attendees.find((_att) => _att.id === b.id);
                                        if (!a_attendee && !b_attendee) return 0;
                                        if (!a_attendee) return 1;
                                        if (!b_attendee) return -1;
                                        const aHasNickname = a_attendee.nickname?.trim() || '';
                                        const bHasNickname = b_attendee.nickname?.trim() || '';
                                        return aHasNickname && bHasNickname
                                            ? a_attendee.nickname.toLowerCase().localeCompare(b_attendee.nickname.toLowerCase())
                                            : (a_attendee.fname || '').localeCompare(b_attendee.fname || '');
                                    })
                                    .map((_a) => {
                                        const attendee = PA_GlobalObject.Planning.attendees.find((_att) => _att.id === _a.id);
                                        const attendeeAvatar = attendeesReferenceArray.find((_att) => _att.id === _a.id);
                                        if (!attendee) return null;

                                        const fullName = attendee.nickname?.trim()
                                            ? `${formatName(attendee.nickname)}`
                                            : `${formatName(attendee.fname)} ${formatName(attendee.lname)}`;

                                        const customAvatarStyles = {
                                            border: `1.5px solid ${stringToColor(fullName)}`,
                                        };

                                        return (
                                            <Tooltip key={_a.id} title={fullName} arrow>
                                                {!attendeeAvatar?.avatar || !attendeeAvatar?.avatar.preview ? (
                                                    <Avatar
                                                        {...stringAvatar(`${formatName(attendee.fname)}, ${formatName(attendee.lname)}`)}
                                                        sx={customAvatarStyles}
                                                    />
                                                ) : (
                                                    <Avatar
                                                        src={attendeeAvatar.avatar.preview}
                                                        sx={customAvatarStyles}
                                                    />
                                                )}
                                            </Tooltip>
                                        );
                                    })}
                            </AvatarGroup>
                        </CardActions>
                    </Box>
                </Box>

                {/* Premium Action Menu */}
                <Menu
                    anchorEl={anchorActivityListItemMenu}
                    open={Boolean(anchorActivityListItemMenu)}
                    onClose={handleActivityListItemMenuClose}
                    PaperProps={{ sx: { borderRadius: '14px', width: 240, mt: 1, p: 0.5, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' } }}
                >
                    <Box sx={{ px: 2, py: 1, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '10px', fontWeight: 900, color: '#94a3b8', letterSpacing: 1 }}>SETTINGS</Typography>
                    </Box>
                    {[
                        { key: 'Location', icon: 'icon-map-marker', label: 'Location', color: '#ff6f61' },
                        { key: 'Dates', icon: 'icon-calendar2', label: 'Dates', color: '#d4af37' },
                        { key: 'Costs', icon: 'icon-wallet', label: 'Costs', color: '#2e8b57' },
                        { key: 'Attendees', icon: 'icon-group', label: 'Attendees', color: '#10318f' },
                        { key: 'Details', icon: 'icon-gear', label: 'Details', color: '#6c757d' },
                        { key: 'Photos', icon: 'icon-picture2', label: 'Photos', color: '#8a2be2' },
                    ].map((item) => (
                        <MenuItem key={item.key} onClick={() => handleMenuItemClick(item.label)} sx={{ borderRadius: '8px', mb: 0.2 }}>
                            <ListItemIcon>
                                <Box sx={{ width: 30, height: 30, bgcolor: `${item.color}15`, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className={item.icon} style={{ color: item.color, fontSize: '14px' }} />
                                </Box>
                            </ListItemIcon>
                            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '13px', fontWeight: 600 }} />
                        </MenuItem>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <MenuItem onClick={() => handleMenuItemClick('Delete')} sx={{ borderRadius: '8px', color: '#ef4444' }}>
                        <ListItemIcon>
                            <Box sx={{ width: 30, height: 30, bgcolor: '#fee2e2', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="icon-trash" style={{ color: '#ef4444', fontSize: '14px' }} />
                            </Box>
                        </ListItemIcon>
                        <ListItemText primary="Delete" primaryTypographyProps={{ fontSize: '13px', fontWeight: 600 }} />
                    </MenuItem>
                </Menu>

            </Card>



            {/* Edit Activity Attendees Dialog */}
            <ActivityDialogEditAttendees
                isOpen={isActivitiesDialogEditAttendeesOpen}
                onClose={() => setActivitiesDialogEditAttendeesOpen(false)}
                eventActivity={eventActivity}
                setEventActivity={handleSaveActivityUpdate}
            />

            {/* Edit Activity Costs Dialog */}
            <ActivityDialogEditCosts
                isOpen={isActivitiesDialogEditCostsOpen}
                onClose={() => setActivitiesDialogEditCostsOpen(false)}
                eventActivity={eventActivity}
                setEventActivity={handleSaveActivityUpdate}
            />

            {/* Edit Activity Photos Dialog */}
            <ActivityDialogEditPhotos
                isOpen={isActivitiesDialogEditPhotosOpen}
                onClose={() => setActivitiesDialogEditPhotosOpen(false)}
                eventActivity={eventActivity}
                setEventActivity={handleSaveActivityUpdate}
            />

            {/* Edit Activity Dates Dialog */}
            <ActivityDialogEditDates
                isOpen={isActivitiesDialogEditDatesOpen}
                onClose={() => setActivitiesDialogEditDatesOpen(false)}
                eventActivity={eventActivity}
                setEventActivity={handleSaveActivityUpdate}
            />

            {/* Edit Activity Location Dialog */}
            <ActivityDialogEditLocation
                isOpen={isActivitiesDialogEditLocationOpen}
                onClose={() => setActivitiesDialogEditLocationOpen(false)}
                eventActivity={eventActivity}
                setEventActivity={handleSaveActivityUpdate}
            />

            {/* Edit Activity Details Dialog */}
            <ActivityDialogEditDetails
                isOpen={isActivitiesDialogEditDetailsOpen}
                onClose={() => setActivitiesDialogEditDetailsOpen(false)}
                eventActivity={eventActivity}
                setEventActivity={handleSaveActivityUpdate}
            />

        </div>
    )
}

export default ActivitiesCard;
